import PropTypes from 'prop-types'
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import classNames from 'lib/util/classNames'
import { getUniqueId } from 'lib/util/uniq'
import Item from './Item'
import styles from './TransitionSwap.module.scss'

/**
 * The `TransitionSwap` (todo)
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function TransitionSwap (props) {
  const {
    Tag,
    animateFirst,
    animation,
    children,
    className,
    classNameContent,
    delayHide,
    delayShow,
    duration
  } = props
  const [items, setItems] = useState([])
  const lastLabel = useRef(0)
  const currentItems = useRef([])
  const classNameOutput = useMemo(
    () =>
      classNames([className, styles.swap, styles[`animation-${animation}`]]),
    [className]
  )
  const isFirst = useRef(true)

  /**
   * Handle removing an item from the queue, which is fired
   * after the child `Item` has removed itself
   */
  const handleRemove = useCallback(
    id => {
      currentItems.current = currentItems.current.filter(
        item => item.id !== id
      )

      setItems(currentItems.current)
    },
    [items]
  )

  /**
   * Effect: When the `label` changes generate a new item to
   * create the rolling text effect
   */
  useEffect(() => {
    const id = getUniqueId()
    let direction = 'up'

    if (typeof children === 'number') {
      direction = children < lastLabel.current ? 'down' : 'up'
    }

    currentItems.current = [
      ...currentItems.current.map(item => ({
        ...item,
        isShown: false,
        direction
      })),
      {
        id,
        children,
        direction,
        animateIn: !animateFirst ? !isFirst.current : true
      }
    ]

    setItems(currentItems.current)

    isFirst.current = false
    lastLabel.current = children
  }, [children])

  return (
    <Tag className={classNameOutput}>
      {items.map(item => (
        <Item
          key={item.id}
          className={classNameContent}
          animation={animation}
          {...item}
          duration={duration}
          onRemoved={handleRemove}
          delayShow={delayShow}
          delayHide={delayHide}
        />
      ))}
    </Tag>
  )
}

/** @type {object} */
TransitionSwap.propTypes = {
  Tag: PropTypes.elementType,
  className: PropTypes.string,
  classNameContent: PropTypes.string,
  children: PropTypes.node,
  delayShow: PropTypes.number,
  delayHide: PropTypes.number,
  animateFirst: PropTypes.bool,
  animation: PropTypes.string,
  duration: PropTypes.number
}

/** @type {object} */
TransitionSwap.defaultProps = {
  Tag: 'div',
  className: '',
  classNameContent: '',
  children: [],
  delayShow: 0,
  delayHide: 0,
  animateFirst: true,
  animation: 'standard',
  duration: 0.25
}

// Memoize
export default memo(TransitionSwap)
